// Solving an exercise (spec §8): serve, judge each move on the server, play
// black's replies and the extra black defences, then the result card.
// The client never has the solution before the attempt is over.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { api, ApiError, type AttemptResult, type LegalMove, type MoveResult, type Position, type Solution } from "../api/client";
import { useHasModules, useLabels, useLoggedIn, usePatchMe } from "../api/hooks";
import { Board, type Arrow, type BoardHandle, type MoveMeta } from "../board/Board";
import { useT } from "../i18n";
import { setPrefs, usePrefs } from "../lib/prefs";
import { apply } from "../lib/textfen";
import { playSolved } from "../lib/sound";
import { LabelPicker } from "./LabelPicker";
import { MoveInputSettings } from "./MoveInputSettings";
import { legalMoves, loadMovegen } from "../wasm/movegen";
import { ReportDialog } from "./ReportDialog";
import { ResultCard } from "./ResultCard";
import { Seed } from "./Seed";
import { SolutionView } from "./SolutionView";

export type TrainMode = "rated" | "module" | "mistakes" | "favorites" | "daily" | "taster";
type Phase = "loading" | "seed" | "solving" | "busy" | "done" | "exhausted" | "error";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
// after a solved exercise the next one comes by itself (unless the user stays)
const AUTO_NEXT_MS = 2200;
const device = () => (window.matchMedia?.("(pointer: coarse)").matches ? "mobile" : "desktop");
const PREFETCH: TrainMode[] = ["rated", "module", "mistakes", "favorites", "taster"];

function legalFor(textfen: string, fromServer: LegalMove[]): LegalMove[] {
  return legalMoves(textfen) ?? fromServer;
}

type Props = { mode: TrainMode; moduleId?: number; theme?: string; retryExercise?: number; compact?: boolean; onTasterDone?: () => void };

export function Trainer({ mode, moduleId, theme: themeProp, retryExercise, compact = false, onTasterDone }: Props) {
  const t = useT();
  const prefs = usePrefs();
  // rated training: all combinations, or only those with the chosen label
  const labelPick = mode === "rated" && !themeProp && !compact;
  const theme = themeProp ?? (labelPick && prefs.train_label ? prefs.train_label : undefined);
  const labels = useLabels();
  const hasModules = useHasModules();
  const loggedIn = useLoggedIn();
  const qc = useQueryClient();
  const patchMe = usePatchMe();
  const board = useRef<BoardHandle>(null);

  const [phase, setPhase] = useState<Phase>("loading");
  const [pos, setPos] = useState<Position | null>(null);
  const [textfen, setTextfen] = useState<string>("W" + "_".repeat(50));
  const [legal, setLegal] = useState<LegalMove[]>([]);
  const [lastMove, setLastMove] = useState<{ from: number; to: number } | null>(null);
  const [arrows, setArrows] = useState<Arrow[]>([]);
  const [banner, setBanner] = useState<string | null>(null);
  const [linesDone, setLinesDone] = useState(0);
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [solution, setSolution] = useState<Solution | null>(null);
  const [favorite, setFavorite] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readyAt = useRef(0);
  const hidden = useRef({ since: 0, total: 0 });
  const prefetched = useRef<Promise<Position> | null>(null);
  const attemptRef = useRef<number | null>(null);

  const request = useCallback(
    (retryOf?: number) =>
      retryOf
        ? api<Position>(`/exercises/${retryOf}/retry`, { method: "POST" })
        : api<Position>("/train/next", {
            body: {
              mode,
              module_id: moduleId ?? null,
              difficulty: mode === "rated" || mode === "module" ? prefs.difficulty : null,
              device: device(),
              theme: theme ?? null,
            },
          }),
    [mode, moduleId, theme, prefs.difficulty],
  );

  const show = useCallback((p: Position) => {
    setResult(null);
    setSolution(null);
    setArrows([]);
    setBanner(null);
    setLastMove(null);
    setLinesDone(0);
    if (p.exhausted || !p.attempt_id || !p.textfen) {
      setPos(p);
      setPhase("exhausted");
      if (p.taster_done) onTasterDone?.();
      return;
    }
    attemptRef.current = p.attempt_id;
    hidden.current = { since: document.hidden ? performance.now() : 0, total: 0 };
    setPos(p);
    setFavorite(p.is_favorite);
    setTextfen(p.textfen);
    if (!p.intro) {
      setLegal(legalFor(p.textfen, p.legal));
      setPhase("solving");
      return;
    }
    // Black to move: the site plays black's first move as an introduction,
    // then it is white's turn. The clock starts when the user can move.
    const intro = p.intro;
    const after = apply(p.textfen, intro);
    setLegal([]);
    setPhase("busy");
    setBanner(t("train.intro", { move: intro.move }));
    void (async () => {
      await sleep(700);
      if (attemptRef.current !== p.attempt_id) return;
      await board.current?.play(intro);
      if (attemptRef.current !== p.attempt_id) return;
      setLastMove({ from: intro.from, to: intro.to });
      setTextfen(after);
      setLegal(legalFor(after, p.legal));
      readyAt.current = performance.now();
      setPhase("solving");
    })();
  }, [onTasterDone, t]);

  const load = useCallback(
    async (retryOf?: number) => {
      setPhase("loading");
      setError(null);
      try {
        const pending = !retryOf && prefetched.current;
        prefetched.current = null;
        show(await (pending || request(retryOf)));
      } catch (e) {
        if (e instanceof ApiError && e.detail === "placement_seed_required") setPhase("seed");
        else {
          setError(e instanceof ApiError ? e.detail : String(e));
          setPhase("error");
        }
      }
    },
    [request, show],
  );

  const started = useRef<string | null>(null);
  useEffect(() => {
    const key = `${mode}|${moduleId}|${loggedIn}|${retryExercise}`;
    if (started.current === key) return; // StrictMode runs effects twice
    started.current = key;
    void loadMovegen();
    prefetched.current = null;
    void load(retryExercise);
  }, [mode, moduleId, loggedIn]); // a new session of this kind; `load` itself changes with prefs

  // §8.2: the clock starts on first paint; the server learns it was shown
  useLayoutEffect(() => {
    if (phase !== "solving" || !pos?.attempt_id) return;
    const id = pos.attempt_id;
    requestAnimationFrame(() => {
      readyAt.current = performance.now();
      void api(`/attempts/${id}/events`, { body: { shown: true, hidden_ms: 0 } }).catch(() => {});
    });
  }, [pos?.attempt_id]); // once per exercise

  // hidden tab time (over 60 s makes the attempt unrated, §6.4)
  useEffect(() => {
    const onVis = () => {
      const h = hidden.current;
      if (document.hidden) h.since = performance.now();
      else if (h.since) {
        h.total += performance.now() - h.since;
        h.since = 0;
        const id = attemptRef.current;
        if (id && phase !== "done") void api(`/attempts/${id}/events`, { body: { hidden_ms: Math.round(h.total) } }).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [phase]);

  const finish = useCallback(
    (r: AttemptResult) => {
      setResult(r);
      setPhase("done");
      setLegal([]);
      if (loggedIn) {
        qc.invalidateQueries({ queryKey: ["dashboard"] });
        qc.invalidateQueries({ queryKey: ["me"] });
      }
      // §14.1: the next exercise is fetched while the card is shown
      if (PREFETCH.includes(mode) && !(mode === "taster" && pos?.taster && pos.taster.index >= pos.taster.total)) {
        prefetched.current = request();
        prefetched.current.catch(() => (prefetched.current = null));
      }
    },
    [loggedIn, mode, pos, qc, request],
  );

  // the board calls this when a move counts: after the 0.5 s grace period in
  // which a slip can be replaced (move-input-spec §7). The board has already
  // shown it.
  const onMove = useCallback(
    async (move: LegalMove, meta: MoveMeta) => {
      if (!pos?.attempt_id || phase !== "solving") return;
      const before = textfen;
      setTextfen(apply(before, move)); // where the board's own animation ends
      setPhase("busy");
      setBanner(null);
      setArrows([]);
      setLastMove({ from: move.from, to: move.to });
      // thinking time runs to the moment the move that counts was entered
      const ms = Math.max(0, Math.round(meta.enteredAt - readyAt.current));
      let res: MoveResult;
      try {
        res = await api<MoveResult>(`/attempts/${pos.attempt_id}/move`, {
          body: { move: move.move, ms, replaced: meta.replaced },
        });
      } catch (e) {
        await board.current?.idle();
        setTextfen(before);
        setError(e instanceof ApiError ? e.detail : String(e));
        setPhase("error");
        return;
      }
      await board.current?.idle();

      if (res.verdict === "wrong") {
        // the wrong move gets a red arrow and is taken back; calm, no shake
        setArrows([{ from: move.from, to: move.to, path: move.path, color: "var(--error)" }]);
        await sleep(650);
        setTextfen(before);
        setLastMove(null);
        if (res.result) finish(res.result);
        return;
      }
      setLinesDone(res.lines_done ?? 0);
      if (res.reply) {
        await sleep(120);
        const reply = res.reply;
        await board.current?.play(reply);
        setLastMove({ from: reply.from, to: reply.to });
      }
      if (res.reset) {
        await sleep(res.reply ? 300 : 500);
        setBanner(t("train.black_also", { move: res.reset.label }));
        setLastMove(null);
        setTextfen(res.reset.textfen);
        await board.current?.idle();
        await sleep(650);
        await board.current?.play(res.reset.reply);
        setLastMove({ from: res.reset.reply.from, to: res.reset.reply.to });
      }
      setTextfen(res.textfen);
      if (res.done && res.result) {
        if (res.result.solved) {
          board.current?.glow("success");
          playSolved();
        }
        finish(res.result);
        return;
      }
      setLegal(legalFor(res.textfen, res.legal));
      readyAt.current = performance.now();
      setPhase("solving");
    },
    [pos, phase, textfen, finish, t],
  );

  const showSolution = useCallback(async () => {
    if (!pos?.attempt_id) return;
    setSolution(await api<Solution>(`/attempts/${pos.attempt_id}/solution`));
  }, [pos]);

  const giveUp = useCallback(async () => {
    if (!pos?.attempt_id || phase !== "solving") return;
    setPhase("busy");
    const r = await api<AttemptResult>(`/attempts/${pos.attempt_id}/give-up`, { method: "POST" });
    finish(r);
    await showSolution();
  }, [pos, phase, finish, showSolution]);

  const toggleFavorite = useCallback(async () => {
    if (!pos?.exercise_id || !loggedIn) return;
    const on = !favorite;
    setFavorite(on);
    await api(`/me/favorites/${pos.exercise_id}`, { method: on ? "PUT" : "DELETE" });
    qc.invalidateQueries({ queryKey: ["favorites"] });
  }, [pos, favorite, loggedIn, qc]);

  const next = useCallback(() => void load(), [load]);

  // solved: on to the next exercise after a moment, unless the user does
  // something with the result first (the card's buttons, a key)
  const [autoNext, setAutoNext] = useState(false);
  const autoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextRef = useRef(next);
  nextRef.current = next;
  const stay = useCallback(() => {
    if (autoTimer.current) clearTimeout(autoTimer.current);
    autoTimer.current = null;
    setAutoNext(false);
  }, []);
  useEffect(() => {
    const go =
      phase === "done" && !!result?.solved && prefs.auto_next && !result.placement?.done && !compact;
    if (!go) return;
    setAutoNext(true);
    autoTimer.current = setTimeout(() => {
      autoTimer.current = null;
      setAutoNext(false);
      nextRef.current();
    }, AUTO_NEXT_MS);
    return () => {
      if (autoTimer.current) clearTimeout(autoTimer.current);
      autoTimer.current = null;
      setAutoNext(false);
    };
  }, [phase, result, prefs.auto_next, compact]);
  const retry = useCallback(() => {
    if (pos?.exercise_id && loggedIn) void load(pos.exercise_id);
  }, [pos, loggedIn, load]);

  // keyboard (§8.4): N next, R retry, F favorite
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.metaKey || e.ctrlKey || reporting) return;
      const k = e.key.toLowerCase();
      if (k !== "n") stay();
      if (k === "n" && (phase === "done" || phase === "exhausted")) next();
      else if (k === "r" && phase === "done") retry();
      else if (k === "f") void toggleFavorite();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, next, retry, toggleFavorite, reporting]);

  function setLabel(label: string) {
    setPrefs({ train_label: label });
    if (loggedIn) patchMe.mutate({ settings: { train_label: label } });
  }

  // a new label starts a new exercise
  const labelKey = useRef(theme);
  useEffect(() => {
    if (labelKey.current === theme) return;
    labelKey.current = theme;
    prefetched.current = null;
    void load();
  }, [theme, load]);

  function setDifficulty(d: "easier" | "normal" | "harder") {
    setPrefs({ difficulty: d });
    if (loggedIn) patchMe.mutate({ settings: { difficulty: d } });
  }

  if (phase === "seed") return <Seed onDone={() => void load()} />;

  if (phase === "exhausted") {
    // rated training on one label: say which, and offer all combinations again
    const labelDone = labelPick && !!prefs.train_label;
    const empty =
      mode === "mistakes"
        ? t("train.mistakes_empty")
        : mode === "favorites"
          ? t("train.favorites_empty")
          : labelDone
            ? t("train.label_done", { label: prefs.train_label })
            : t("train.exhausted");
    return (
      <div className="card stack" style={{ textAlign: "center", padding: 28 }}>
        {pos?.taster_done ? (
          <>
            <h2 style={{ color: "var(--text)" }}>{t("landing.taster_done")}</h2>
            <div className="row" style={{ justifyContent: "center" }}>
              <Link className="btn primary" to="/register">{t("auth.signup")}</Link>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ color: "var(--text)" }}>{empty}</h2>
            {mode !== "mistakes" && mode !== "favorites" && !labelDone && <p className="muted">{t("train.exhausted_hint")}</p>}
            <div className="row" style={{ justifyContent: "center" }}>
              {loggedIn && <Link className="btn" to="/train/mistakes">{t("train.mistakes")}</Link>}
              {labelDone ? (
                <button className="primary" onClick={() => setLabel("")}>
                  {t("train.all_labels", { n: labels.data?.total ?? "" })}
                </button>
              ) : (
                hasModules && <Link className="btn primary" to="/modules">{t("nav.modules")}</Link>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  const label =
    pos?.label === "second_chance"
      ? t("train.second_chance")
      : pos?.label === "review"
        ? t("train.review")
        : pos?.daily
          ? t("train.daily")
          : theme ?? pos?.module ?? (pos?.taster ? t("train.taster", { i: pos.taster.index, n: pos.taster.total }) : t("train.white_to_play"));
  const placement = result?.placement ?? pos?.placement ?? null;
  const done = phase === "done";

  return (
    <div className="train-col">
      {!compact && (
        <div className="toolbar">
          {banner && !done ? (
            <span className="title" role="status" style={{ color: "var(--accent)" }}>{banner}</span>
          ) : (
            <span className="title">{label}</span>
          )}
          {done && result?.exercise_rating && (
            <span className="pill num">{t("result.exercise_rating", { r: result.exercise_rating.after })}</span>
          )}
          {(pos?.lines_total ?? 1) > 1 && !done && (
            <span className="pill">{t("train.lines", { i: Math.min(linesDone + 1, pos!.lines_total), n: pos!.lines_total })}</span>
          )}
          <span className="spacer" />
          {labelPick && labels.data && labels.data.labels.length > 0 && (
            <LabelPicker
              value={prefs.train_label}
              total={labels.data.total}
              labels={labels.data.labels}
              onChange={setLabel}
            />
          )}
          {(pos?.mode === "rated" || (pos?.mode === "module" && pos.rated)) && !placement && (
            <select
              aria-label="difficulty"
              value={prefs.difficulty}
              onChange={(e) => setDifficulty(e.target.value as "easier" | "normal" | "harder")}
            >
              <option value="easier">{t("train.easier")}</option>
              <option value="normal">{t("train.normal")}</option>
              <option value="harder">{t("train.harder")}</option>
            </select>
          )}
          <MoveInputSettings />
          <button className="ghost" title={t("train.sound")} aria-pressed={prefs.sound} onClick={() => setPrefs({ sound: !prefs.sound })}>
            {prefs.sound ? "🔊" : "🔇"}
          </button>
          {loggedIn && pos?.exercise_id && (
            <button className="ghost" onClick={toggleFavorite} title={favorite ? t("train.unfavorite") : t("train.favorite")} aria-pressed={favorite}>
              {favorite ? "★" : "☆"}
            </button>
          )}
          {loggedIn && pos?.exercise_id && (
            <button className="ghost" onClick={() => setReporting(true)} title={t("train.report")}>⚑</button>
          )}
        </div>
      )}

      {placement && !placement.done && (
        <div className="strip" aria-label={t("train.placement", { i: placement.index, n: placement.total })}>
          <span>{t("train.placement", { i: placement.index, n: placement.total })}</span>
          <span className="dots">
            {Array.from({ length: placement.total }, (_, i) => (
              <span key={i} className={`dot ${i < placement.index - (done ? 0 : 1) ? "done" : i === placement.index - 1 ? "now" : ""}`} />
            ))}
          </span>
        </div>
      )}

      {compact && (
        <div className="small" role="status" style={{ minHeight: "1.5em", color: "var(--accent)", fontWeight: 600 }}>
          {!done ? banner : null}
        </div>
      )}

      {solution ? (
        <SolutionView solution={solution} />
      ) : (
        <div style={{ position: "relative" }}>
          <Board
            ref={board}
            position={textfen}
            legal={phase === "solving" ? legal : []}
            onMove={onMove}
            lastMove={lastMove}
            arrows={arrows}
            coordinates={prefs.coordinates}
            label={t("train.white_to_play")}
            assist={prefs.move_assist}
            drag={prefs.move_drag}
            showTargets={prefs.move_targets}
          />
        </div>
      )}

      {phase === "loading" && !pos && <p className="muted" style={{ textAlign: "center" }}>{t("train.loading")}</p>}
      {phase === "error" && (
        <div className="card row">
          <span className="err">{error === "login_required" ? t("nav.login") : t("err.generic")}</span>
          <span className="spacer" />
          <button onClick={() => void load()}>{t("result.next")}</button>
        </div>
      )}

      {(phase === "solving" || phase === "busy") && !compact && (
        <div className="row" style={{ justifyContent: "center" }}>
          <button className="ghost small muted" onClick={giveUp} disabled={phase !== "solving"}>
            {t("train.give_up")}
          </button>
        </div>
      )}

      {done && result && (
        <ResultCard
          autoNextMs={autoNext ? AUTO_NEXT_MS : null}
          onStay={stay}
          result={result}
          isFavorite={favorite}
          canFavorite={loggedIn}
          canRetry={loggedIn}
          onFavorite={toggleFavorite}
          onReplay={showSolution}
          onNext={next}
          onRetry={retry}
          onReport={loggedIn ? () => setReporting(true) : undefined}
          showingSolution={!!solution}
        />
      )}

      {reporting && pos?.exercise_id && (
        <ReportDialog exerciseId={pos.exercise_id} attemptId={pos.attempt_id} onClose={() => setReporting(false)} />
      )}
    </div>
  );
}
